import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Zap, Upload, Trash2, Plus, Users, Archive, RotateCw, Eye, X, Clock, Inbox, Settings, HelpCircle, UserCheck, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import CreatorTour from "../components/CreatorTour";
import { GLIMT_TOUR_STEPS } from "../components/tour/steps_glimt";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import AuthImage from "../components/AuthImage";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function GlimtStudio() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [archived, setArchived] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [glimtActive, setGlimtActive] = useState(null); // null=laster, true/false=avklart

  const loadActive = async () => {
    try {
      const r = await api.get("/me/glimt-active");
      setGlimtActive(r.data.active);
    } catch { setGlimtActive(false); }
  };

  const toggleActive = async () => {
    try {
      const next = !glimtActive;
      await api.patch("/me/glimt-active", { active: next });
      setGlimtActive(next);
      toast.success(next ? "Glimt aktivert" : "Glimt deaktivert");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [a, ar, sc, s] = await Promise.all([
        api.get("/me/glimt?archived=false"),
        api.get("/me/glimt?archived=true"),
        api.get("/me/glimt?scheduled=true"),
        api.get("/me/glimt-segments"),
      ]);
      setItems(a.data.items || []);
      setArchived(ar.data.items || []);
      setScheduled(sc.data.items || []);
      setSegments(s.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) { loadActive(); load(); } }, [user]);

  if (!user) return <Layout><div className="p-8">Logg inn</div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="glimt-studio">
        <Link to="/skaper/studio" className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5 mb-6">
          <ArrowLeft className="h-4 w-4" /> Tilbake
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <Zap className="h-6 w-6 text-[var(--magenta)]" />
          <h1 className="font-fraunces italic text-3xl sm:text-4xl text-[var(--cream)]">Glimt-studio</h1>
          <button
            onClick={() => setShowTour(true)}
            data-testid="glimt-show-tour-btn"
            className="ml-auto px-3 py-1.5 border border-white/15 text-[var(--cream-soft)] hover:text-[var(--magenta)] hover:border-[var(--magenta)]/40 font-meta text-xs inline-flex items-center gap-1.5"
          >
            <HelpCircle className="h-3.5 w-3.5" /> {t("tour_glimt.show_again", "Vis omvisning")}
          </button>
        </div>
        <p className="font-manrope text-sm text-[var(--cream-soft)] mb-6">
          Snap-lignende ephemeral innhold. Publiser åpent til følgere eller lukket til segmenter.
        </p>

        {/* Aktiver Glimt-toggle */}
        <div className="mb-6 border border-white/10 p-4 flex items-center gap-3" data-testid="glimt-active-panel">
          <div className="flex-1">
            <div className="font-fraunces italic text-lg text-[var(--cream)]">Glimt aktivt for din profil</div>
            <div className="text-xs text-[var(--cream-faint)] mt-1">
              {glimtActive === false
                ? "Fans kan ikke legge deg til eller motta Glimt før du aktiverer."
                : glimtActive === true
                  ? "Fans kan finne deg i søk og legge deg til som Glimt-venn."
                  : "Laster…"}
            </div>
          </div>
          <button
            onClick={toggleActive}
            disabled={glimtActive === null}
            className={`relative w-14 h-8 rounded-full transition-colors ${glimtActive ? "bg-[var(--magenta)]" : "bg-white/15"}`}
            data-testid="glimt-active-toggle"
            aria-label={glimtActive ? "Slå av Glimt" : "Slå på Glimt"}
          >
            <span
              className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-black transition-transform ${glimtActive ? "translate-x-6" : ""}`}
            />
          </button>
        </div>

        {/* Blokade når ikke aktivert */}
        {glimtActive === false && (
          <div className="border border-[var(--magenta)]/40 bg-[var(--magenta)]/5 p-6 text-center" data-testid="glimt-onboarding">
            <Zap className="h-8 w-8 text-[var(--magenta)] mx-auto mb-3" />
            <h2 className="font-fraunces italic text-xl text-[var(--cream)] mb-2">Klar til å bruke Glimt?</h2>
            <p className="text-sm text-[var(--cream-soft)] max-w-md mx-auto mb-4">
              Aktiver Glimt for at fans skal kunne legge deg til som venn og motta dine daglige Glimt.
            </p>
            <button onClick={toggleActive} className="btn-magenta" data-testid="glimt-onboarding-activate">
              Aktiver Glimt
            </button>
          </div>
        )}

        {glimtActive === true && (
        <>
        <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto">
          {[
            { k: "active", label: "Aktive", ic: Zap },
            { k: "new", label: "Nytt glimt", ic: Plus },
            { k: "scheduled", label: "Planlagt", ic: Clock },
            { k: "segments", label: "Segmenter", ic: Users },
            { k: "fans", label: "Fans", ic: UserCheck },
            { k: "archive", label: "Arkiv", ic: Archive },
            { k: "dm", label: "DM-innstillinger", ic: Settings },
          ].map(t => {
            const Ic = t.ic;
            return (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`px-4 py-2 text-sm font-manrope flex items-center gap-1.5 border-b-2 whitespace-nowrap ${tab === t.k ? "border-[var(--magenta)] text-[var(--cream)]" : "border-transparent text-[var(--cream-soft)]"}`}
                data-testid={`glimt-tab-${t.k}`}>
                <Ic className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "new" && <NewGlimtForm segments={segments} onDone={() => { load(); setTab("active"); }} />}
        {tab === "active" && <GlimtList items={items} loading={loading} onChange={load} segments={segments} archived={false} />}
        {tab === "scheduled" && <ScheduledList items={scheduled} onChange={load} />}
        {tab === "archive" && <GlimtList items={archived} loading={loading} onChange={load} segments={segments} archived={true} />}
        {tab === "segments" && <Segments segments={segments} onChange={load} />}
        {tab === "fans" && <FansPanel segments={segments} onChange={load} />}
        {tab === "dm" && <DMSettingsPanel />}
        </>
        )}

        {showTour && (
          <CreatorTour
            onClose={() => setShowTour(false)}
            completeEndpoint="/me/glimt-tour-completed"
            steps={GLIMT_TOUR_STEPS.map((s) => ({
              ...s,
              title: t(s.titleKey),
              body: t(s.bodyKey),
            }))}
          />
        )}
      </div>
    </Layout>
  );
}

function NewGlimtForm({ segments, onDone }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [duration, setDuration] = useState(24);
  const [visibility, setVisibility] = useState("followers");
  const [segIds, setSegIds] = useState([]);
  const [archiveOnly, setArchiveOnly] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [replayMode, setReplayMode] = useState("once");
  const scheduledAt = scheduledDate && scheduledTime ? `${scheduledDate}T${scheduledTime}` : "";
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Velg fil"); return; }
    setBusy(true);
    try {
      const scheduledIso = (() => {
        if (!scheduledAt || archiveOnly) return "";
        const dt = new Date(scheduledAt);
        return isNaN(dt.getTime()) ? "" : dt.toISOString();
      })();

      const isVideo = (file.type || "").startsWith("video/");
      // Direct-to-R2 for video >20 MB → omgår Cloudflare/ingress 413
      const useDirect = isVideo && file.size > 20 * 1024 * 1024;

      if (useDirect) {
        // Steg 1: init
        const init = await api.post("/me/glimt/init-upload", {
          content_type: file.type || "video/mp4",
          size: file.size,
          filename: file.name,
          caption,
          duration_hours: parseInt(duration, 10) || 24,
          visibility,
          segment_ids: visibility === "segments" ? segIds : [],
          scheduled_at: scheduledIso || null,
          archive_only: archiveOnly,
          replay_mode: replayMode,
        });
        const { video_id, upload_url } = init.data || {};
        if (!video_id || !upload_url) throw new Error("Init-upload feilet");

        // Steg 2: PUT direkte til R2
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", upload_url);
          xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
            ? resolve()
            : reject(new Error(`R2 PUT feilet: ${xhr.status}`));
          xhr.onerror = () => reject(new Error("R2 PUT nettverksfeil (sjekk R2 CORS)"));
          xhr.send(file);
        });

        // Steg 3: finalize (returnerer umiddelbart, prosessering fortsetter i bakgrunn)
        const fin = await api.post("/me/glimt/finalize", { video_id });
        if (fin.data?.processing) {
          toast.success(archiveOnly
            ? "Lagret. Prosesseres i bakgrunn — dukker opp i arkivet om et par minutter."
            : (scheduledIso
              ? "Planlagt. Prosesseres i bakgrunn — publiseres til avtalt tid."
              : "Sendt inn. Prosesseres i bakgrunn — dukker opp om et par minutter."));
          onDone();
          return;
        }
      } else {
        // Gammel multipart-flyt: bilder og korte videoer (<20 MB)
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", caption);
        fd.append("duration_hours", duration);
        fd.append("visibility", visibility);
        fd.append("segment_ids", segIds.join(","));
        fd.append("archive_only", archiveOnly ? "true" : "false");
        if (scheduledIso) fd.append("scheduled_at", scheduledIso);
        fd.append("replay_mode", replayMode);
        await api.post("/me/glimt/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }

      toast.success(archiveOnly ? "Lagret i arkiv" : (scheduledIso ? "Planlagt" : "Publisert"));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[var(--magenta)]/30 p-6 space-y-4" data-testid="glimt-new-form">
      <div>
        <label className="font-manrope text-sm text-[var(--cream)] block mb-2">Bilde eller video *</label>
        <input type="file" accept="image/*,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="font-manrope text-sm text-[var(--cream)]" data-testid="glimt-new-file" />
      </div>
      <div>
        <label className="font-manrope text-sm text-[var(--cream)] block mb-2">Bildetekst</label>
        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={280} rows={2}
          className="input-editorial w-full" data-testid="glimt-new-caption" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="font-manrope text-sm text-[var(--cream)] block mb-2">Varighet</label>
          <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="input-editorial w-full">
            <option value={12}>12 timer</option>
            <option value={24}>24 timer</option>
            <option value={48}>48 timer</option>
            <option value={72}>72 timer</option>
            <option value={168}>1 uke</option>
          </select>
        </div>
        <div>
          <label className="font-manrope text-sm text-[var(--cream)] block mb-2">Synlighet</label>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="input-editorial w-full" data-testid="glimt-new-visibility">
            <option value="followers">Alle mine følgere</option>
            <option value="segments">Kun valgte segmenter</option>
          </select>
        </div>
      </div>

      {/* Visnings-modus */}
      <div>
        <label className="font-manrope text-sm text-[var(--cream)] block mb-2">Visning</label>
        <div className="grid sm:grid-cols-2 gap-2">
          <label
            className={`border p-3 cursor-pointer transition ${
              replayMode === "once" ? "border-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 hover:border-white/30"
            }`}
            data-testid="glimt-replay-once"
          >
            <input
              type="radio"
              name="replay_mode"
              value="once"
              checked={replayMode === "once"}
              onChange={() => setReplayMode("once")}
              className="mr-2"
            />
            <span className="font-manrope text-sm text-[var(--cream)]">Se én gang</span>
            <div className="text-[11px] text-[var(--cream-faint)] mt-1">
              Fansen kan åpne én gang. Deretter forsvinner den fra listen deres.
            </div>
          </label>
          <label
            className={`border p-3 cursor-pointer transition ${
              replayMode === "until_expires" ? "border-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 hover:border-white/30"
            }`}
            data-testid="glimt-replay-until-expires"
          >
            <input
              type="radio"
              name="replay_mode"
              value="until_expires"
              checked={replayMode === "until_expires"}
              onChange={() => setReplayMode("until_expires")}
              className="mr-2"
            />
            <span className="font-manrope text-sm text-[var(--cream)]">Vises til utløp</span>
            <div className="text-[11px] text-[var(--cream-faint)] mt-1">
              Fansen kan se den så mange ganger de vil inntil varigheten er ute.
            </div>
          </label>
        </div>
      </div>
      {visibility === "segments" && (
        <div>
          <label className="font-manrope text-sm text-[var(--cream)] block mb-2">Velg segmenter</label>
          <div className="flex gap-2 flex-wrap">
            {segments.length === 0 && <span className="text-xs text-[var(--cream-faint)]">Ingen segmenter ennå — opprett i Segmenter-fanen</span>}
            {segments.map(s => {
              const on = segIds.includes(s.id);
              return (
                <button key={s.id} type="button" onClick={() => setSegIds(on ? segIds.filter(x => x !== s.id) : [...segIds, s.id])}
                  className={`px-3 py-1.5 text-xs border ${on ? "bg-[var(--magenta)]/20 border-[var(--magenta)] text-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"}`}>
                  {s.name} <span className="opacity-60">({s.member_count})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-[var(--cream)]">
        <input type="checkbox" checked={archiveOnly} onChange={(e) => setArchiveOnly(e.target.checked)} className="accent-[var(--magenta)]" data-testid="glimt-new-archive" />
        Lagre kun i arkiv (ikke publiser nå — for republisering senere)
      </label>
      {!archiveOnly && (
        <div>
          <label className="font-manrope text-sm text-[var(--cream)] block mb-2">Planlagt publisering <span className="text-[var(--cream-faint)] text-xs">(valgfritt)</span></label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-manrope text-[11px] uppercase tracking-wider text-[var(--cream-faint)] block mb-1">📅 Dato</label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="input-editorial w-full"
                data-testid="glimt-new-scheduled-date"
              />
            </div>
            <div>
              <label className="font-manrope text-[11px] uppercase tracking-wider text-[var(--cream-faint)] block mb-1">🕐 Klokkeslett</label>
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="input-editorial w-full"
                data-testid="glimt-new-scheduled-time"
              />
            </div>
          </div>
          {scheduledAt && (
            <p className="text-xs text-[var(--cream-faint)] mt-2">
              Publiseres automatisk {new Date(scheduledAt).toLocaleString("nb-NO")}. La feltene være tomme for umiddelbar publisering.
            </p>
          )}
        </div>
      )}
      <button onClick={submit} disabled={busy || !file} className="btn-magenta inline-flex items-center gap-2" data-testid="glimt-new-submit">
        <Upload className="h-4 w-4" /> {busy ? "Laster opp…" : (scheduledAt && !archiveOnly ? "Planlegg" : "Publiser")}
      </button>
    </div>
  );
}

function GlimtList({ items, loading, onChange, segments, archived }) {
  if (loading) return <p className="text-[var(--cream-soft)]">Laster…</p>;
  if (items.length === 0) return <p className="font-fraunces italic text-[var(--cream-soft)]">{archived ? "Ingen arkiverte glimt." : "Ingen aktive glimt."}</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map(g => <GlimtCard key={g.id} g={g} onChange={onChange} segments={segments} archived={archived} />)}
    </div>
  );
}

function GlimtCard({ g, onChange, segments, archived }) {
  const [viewsOpen, setViewsOpen] = useState(false);
  const del = async () => {
    if (!window.confirm("Slett dette glimtet?")) return;
    await api.delete(`/me/glimt/${g.id}`); toast.success("Slettet"); onChange();
  };
  const arch = async () => {
    await api.patch(`/me/glimt/${g.id}`, { archived: true }); toast.success("Arkivert"); onChange();
  };
  const republish = async () => {
    await api.post(`/me/glimt/${g.id}/republish?hours=24`); toast.success("Publisert på nytt (24t)"); onChange();
  };
  const isVideo = g.media_type === "video";
  const segNames = (g.segment_ids || []).map(id => segments.find(s => s.id === id)?.name).filter(Boolean).join(", ");
  return (
    <div className="border border-white/10 relative" data-testid={`glimt-card-${g.id}`}>
      <div className="aspect-[9/16] bg-black relative">
        {isVideo ? (
          <video src={`${process.env.REACT_APP_BACKEND_URL}/api/glimt/${g.id}/media`} className="w-full h-full object-cover" muted />
        ) : (
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/glimt/${g.id}/media`} alt="" className="w-full h-full object-cover" />
        )}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
              <div className="w-0 h-0 border-l-[10px] border-l-white border-y-[6px] border-y-transparent ml-0.5" />
            </div>
          </div>
        )}
        <div className="absolute top-1 right-1 text-[9px] uppercase px-1.5 py-0.5 bg-black/70 text-white flex items-center gap-1">
          <Eye className="h-2.5 w-2.5" /> {g.view_count || 0}
        </div>
      </div>
      <div className="p-2">
        <p className="font-manrope text-xs text-[var(--cream)] line-clamp-2">{g.caption || <span className="text-[var(--cream-faint)] italic">Ingen tekst</span>}</p>
        <p className="text-[10px] text-[var(--cream-faint)] mt-1">
          {g.visibility === "followers" ? "Alle følgere" : (segNames || "Segmenter")}
        </p>
        {g.expires_at && !archived && <p className="text-[10px] text-[var(--cream-faint)]">Utløper {new Date(g.expires_at).toLocaleString("nb-NO")}</p>}
        <div className="flex gap-1 mt-2">
          {!archived && <button onClick={() => setViewsOpen(true)} className="text-[10px] px-2 py-1 border border-white/15 text-[var(--cream-soft)] hover:text-[var(--magenta)]">Visninger</button>}
          {!archived && <button onClick={arch} className="text-[10px] px-2 py-1 border border-white/15 text-[var(--cream-soft)] hover:text-[var(--magenta)]">Arkiv</button>}
          {archived && <button onClick={republish} className="text-[10px] px-2 py-1 border border-[var(--magenta)]/40 text-[var(--magenta)] flex items-center gap-1"><RotateCw className="h-3 w-3"/>Republiser</button>}
          <button onClick={del} className="text-[10px] px-2 py-1 border border-white/15 text-red-300"><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>
      {viewsOpen && <ViewsModal gid={g.id} onClose={() => setViewsOpen(false)} />}
    </div>
  );
}

function ViewsModal({ gid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/me/glimt/${gid}/views`).then(r => setData(r.data)).catch(() => {}); }, [gid]);
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="glimt-views-modal">
      <div className="bg-[var(--noir-elev)] border border-[var(--magenta)]/30 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between p-4 border-b border-white/10">
          <h3 className="font-fraunces italic text-lg text-[var(--cream)]">Visninger & reaksjoner</h3>
          <button onClick={onClose} data-testid="glimt-views-close"><X className="h-5 w-5 text-[var(--cream-soft)]" /></button>
        </div>
        {data && (
          <div className="p-4 space-y-4">
            <p className="text-sm text-[var(--cream-soft)]">
              Totalt: {data.total_views} visninger, {(data.reactions || []).length} reaksjoner
            </p>

            {/* Visninger — brukerliste */}
            {(data.views || []).length > 0 && (
              <div>
                <div className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream-faint)] mb-2">
                  Hvem har sett ({(data.views || []).length})
                </div>
                <div className="divide-y divide-white/5" data-testid="glimt-views-list">
                  {(data.views || []).map(v => (
                    <Link
                      key={v.user_id}
                      to={v.username ? `/profile/${v.username}` : "#"}
                      className="flex items-center gap-3 py-2 hover:bg-white/5 px-1 transition-colors"
                      data-testid={`glimt-viewer-${v.user_id}`}
                    >
                      <div className="h-8 w-8 rounded-sm bg-[var(--magenta)]/20 flex items-center justify-center overflow-hidden shrink-0">
                        {v.avatar_url ? (
                          <img src={v.avatar_url} alt={v.username || ""} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-[10px] font-bold text-[var(--magenta)]">
                            {(v.username || "?").slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[var(--cream)] truncate">
                          @{v.username || "ukjent"}
                        </div>
                        <div className="text-[10px] text-[var(--cream-faint)]">
                          {formatRelative(v.viewed_at)}
                          {v.view_count > 1 && ` · sett ${v.view_count} ganger`}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Reaksjoner */}
            {(data.reactions || []).length > 0 && (
              <div>
                <div className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream-faint)] mb-2">
                  Reaksjoner ({(data.reactions || []).length})
                </div>
                <div className="space-y-2">
                  {(data.reactions || []).map(r => (
                    <div key={r.id} className="border border-white/10 p-2 text-xs text-[var(--cream)]">
                      <div className="flex items-center gap-2 mb-1">
                        {r.username && (
                          <Link to={`/profile/${r.username}`} className="text-[var(--magenta)] hover:underline">
                            @{r.username}
                          </Link>
                        )}
                        <span className="text-[10px] text-[var(--cream-faint)]">
                          {formatRelative(r.created_at)}
                        </span>
                      </div>
                      {r.emoji && <span className="text-lg mr-2">{r.emoji}</span>}
                      {r.message && <span className="italic">{r.message}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Segments({ segments, onChange }) {
  const [name, setName] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    await api.post("/me/glimt-segments", { name: name.trim() });
    setName(""); onChange();
  };
  const del = async (id) => {
    if (!window.confirm("Slett segment (medlemmer beholder ikke tilgang)?")) return;
    await api.delete(`/me/glimt-segments/${id}`); onChange();
  };
  return (
    <div>
      <div className="flex gap-2 mb-6">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Navn på segment (f.eks. VIP)" className="input-editorial flex-1" data-testid="glimt-seg-name" />
        <button onClick={create} className="btn-magenta" data-testid="glimt-seg-create">Opprett</button>
      </div>
      <div className="space-y-3">
        {segments.map(s => <SegmentRow key={s.id} seg={s} onDelete={() => del(s.id)} onChange={onChange} />)}
        {segments.length === 0 && <p className="font-fraunces italic text-[var(--cream-soft)]">Ingen segmenter ennå.</p>}
      </div>
    </div>
  );
}

function SegmentRow({ seg, onDelete, onChange }) {
  const [members, setMembers] = useState([]);
  const [username, setUsername] = useState("");
  const [open, setOpen] = useState(false);
  const loadMembers = async () => {
    const r = await api.get(`/me/glimt-segments/${seg.id}/members`); setMembers(r.data.items || []);
  };
  useEffect(() => { if (open) loadMembers(); }, [open]);
  const add = async () => {
    if (!username.trim()) return;
    try { await api.post(`/me/glimt-segments/${seg.id}/members`, { username: username.trim() }); setUsername(""); loadMembers(); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rem = async (uid) => {
    await api.delete(`/me/glimt-segments/${seg.id}/members/${uid}`); loadMembers(); onChange();
  };
  return (
    <div className="border border-white/10 p-3">
      <div className="flex justify-between items-center">
        <div>
          <span className="font-fraunces italic text-lg text-[var(--cream)]">{seg.name}</span>
          <span className="text-xs text-[var(--cream-faint)] ml-2">({seg.member_count} medlemmer)</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpen(!open)} className="btn-ghost text-xs">{open ? "Skjul" : "Medlemmer"}</button>
          <button onClick={onDelete} className="text-red-300 text-xs px-2"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex gap-2 mb-3">
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Brukernavn" className="input-editorial flex-1" data-testid={`glimt-seg-add-${seg.id}`} />
            <button onClick={add} className="btn-magenta text-sm">Legg til</button>
          </div>
          <div className="space-y-1">
            {members.map(m => (
              <div key={m.user_id} className="flex justify-between items-center text-sm">
                <span className="text-[var(--cream)]">@{m.username}</span>
                <button onClick={() => rem(m.user_id)} className="text-red-300 text-xs">Fjern</button>
              </div>
            ))}
            {members.length === 0 && <p className="text-xs text-[var(--cream-faint)] italic">Ingen medlemmer.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Planlagt-liste ----
function ScheduledList({ items, onChange }) {
  if (!items.length) return <p className="font-fraunces italic text-[var(--cream-soft)]">Ingen planlagte glimt.</p>;
  const del = async (id) => {
    if (!window.confirm("Slett dette planlagte glimtet?")) return;
    await api.delete(`/me/glimt/${id}`); toast.success("Slettet"); onChange();
  };
  return (
    <div className="space-y-2" data-testid="glimt-scheduled-list">
      {items.map(g => (
        <div key={g.id} className="flex items-center gap-3 p-3 border border-white/10 rounded">
          <div className="w-16 h-16 shrink-0 bg-black/50 flex items-center justify-center overflow-hidden">
            <AuthImage imageId={g.id} src={`/api/glimt/${g.id}/media`} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-[var(--cream)] truncate">{g.caption || "(uten tekst)"}</div>
            <div className="text-xs text-[var(--cream-soft)] flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Publiseres {new Date(g.scheduled_at).toLocaleString("nb-NO")}
            </div>
            <div className="text-[10px] text-[var(--cream-faint)]">
              {g.visibility === "segments" ? `Segment: ${(g.segment_ids || []).length}` : "Alle følgere"} · {g.duration_hours}t varighet
            </div>
          </div>
          <button onClick={() => del(g.id)} className="p-2 text-red-400 hover:text-red-300" data-testid={`glimt-scheduled-del-${g.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ---- DM-innstillinger ----
function DMSettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/me/glimt-dm/settings").then(r => setS(r.data)).catch(() => {});
  }, []);
  if (!s) return <p className="text-[var(--cream-soft)]">Laster…</p>;
  const update = (patch) => {
    const body = { ...patch };
    if ("accepts" in patch) {
      const a = { ...(s.accepts || {}), ...patch.accepts };
      body.accepts_tekst = a.tekst;
      body.accepts_bilde = a.bilde;
      body.accepts_video = a.video;
      delete body.accepts;
    }
    api.patch("/me/glimt-dm/settings", body).then(r => { setS(r.data); toast.success("Lagret"); });
  };
  const a = s.accepts || {};
  return (
    <div className="space-y-6 max-w-xl" data-testid="glimt-dm-settings">
      <div>
        <h3 className="font-fraunces italic text-xl text-[var(--cream)] mb-3">Innkommende glimt fra fans</h3>
        <p className="text-sm text-[var(--cream-soft)] mb-4">
          Fans kan sende glimt til deg. Bestem hva du vil ta imot. Fan-til-fan er alltid blokkert.
        </p>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!s.closed} onChange={(e) => update({ closed: !e.target.checked })}
          className="accent-[var(--magenta)]" data-testid="dm-toggle-open" />
        <div>
          <div className="text-sm text-[var(--cream)]">Åpen for glimt-DM</div>
          <div className="text-xs text-[var(--cream-faint)]">Slå av for å stenge helt.</div>
        </div>
      </label>

      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[var(--cream-soft)] mb-2">Ta imot</div>
        <div className="flex flex-wrap gap-2">
          {[["tekst", "Tekst"], ["bilde", "Bilde"], ["video", "Video"]].map(([k, l]) => (
            <button key={k} onClick={() => update({ accepts: { [k]: !a[k] } })}
              disabled={s.closed}
              className={`px-4 py-2 text-sm border ${a[k] ? "bg-[var(--magenta)]/20 border-[var(--magenta)] text-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"} disabled:opacity-40`}
              data-testid={`dm-accept-${k}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[var(--cream-soft)] mb-2">Slette-policy</div>
        <select value={s.ephemeral || "on_open"} onChange={(e) => update({ ephemeral: e.target.value })}
          className="input-editorial" data-testid="dm-ephemeral">
          <option value="on_open">Slett umiddelbart etter åpning</option>
          <option value="24h">Slett 24 timer etter åpning</option>
        </select>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[var(--cream-soft)] mb-2">
          Grense per fan per døgn (0 = ingen grense)
        </div>
        <input type="number" min={0} max={50} value={s.rate_limit_per_day || 0}
          onChange={(e) => update({ rate_limit_per_day: Number(e.target.value) })}
          className="input-editorial w-32" data-testid="dm-rate-limit" />
      </div>
    </div>
  );
}


// ---- Fans-fane: Glimt-venner + kun-følgere ----
function FansPanel({ segments, onChange }) {
  const [data, setData] = useState({ glimt_friends: [], followers_only: [] });
  const [loading, setLoading] = useState(false);
  const [showFollowers, setShowFollowers] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkSeg, setBulkSeg] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/glimt-fans");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (uid) => {
    setSelected((s) => {
      const ns = new Set(s);
      if (ns.has(uid)) ns.delete(uid); else ns.add(uid);
      return ns;
    });
  };

  const bulk = async (action) => {
    if (!bulkSeg) { toast.error("Velg segment"); return; }
    if (selected.size === 0) { toast.error("Velg minst én fan"); return; }
    try {
      await api.post("/me/glimt-fans/bulk-segment", {
        user_ids: [...selected],
        segment_id: bulkSeg,
        action,
      });
      toast.success(action === "add" ? "Lagt til i segment" : "Fjernet fra segment");
      setSelected(new Set());
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filterFn = (row) => {
    if (!q.trim()) return true;
    return (row.user.username || "").toLowerCase().includes(q.trim().toLowerCase());
  };
  const friends = data.glimt_friends.filter(filterFn);
  const followers = data.followers_only.filter(filterFn);

  if (loading) return <p className="text-[var(--cream-soft)]">Laster…</p>;

  return (
    <div className="space-y-6" data-testid="glimt-fans-panel">
      {/* Søk + bulk */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[var(--cream-faint)] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Søk fans"
            className="input-editorial w-full pl-9"
            data-testid="fans-search"
          />
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2" data-testid="fans-bulk-bar">
            <span className="text-xs text-[var(--cream-soft)]">{selected.size} valgt</span>
            <select
              value={bulkSeg}
              onChange={(e) => setBulkSeg(e.target.value)}
              className="input-editorial text-xs"
              data-testid="fans-bulk-segment"
            >
              <option value="">Velg segment…</option>
              {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={() => bulk("add")} className="btn-magenta text-xs px-3 py-1.5" data-testid="fans-bulk-add">
              Legg til
            </button>
            <button onClick={() => bulk("remove")} className="btn-ghost text-xs px-3 py-1.5" data-testid="fans-bulk-remove">
              Fjern
            </button>
          </div>
        )}
      </div>

      {/* Glimt-venner */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <UserCheck className="w-4 h-4 text-[var(--magenta)]" />
          <h3 className="font-fraunces italic text-lg text-[var(--cream)]">
            Glimt-venner ({data.glimt_friends.length})
          </h3>
        </div>
        {friends.length === 0 ? (
          <p className="text-sm text-[var(--cream-faint)] italic">
            Ingen fans har lagt deg til på Glimt ennå.
          </p>
        ) : (
          <div className="space-y-1">
            {friends.map((row) => <FanRow key={row.user.id} row={row} checked={selected.has(row.user.id)} onToggle={() => toggle(row.user.id)} />)}
          </div>
        )}
      </div>

      {/* Kun følgere (skjulbar) */}
      <div className="border-t border-white/10 pt-4">
        <button
          onClick={() => setShowFollowers(!showFollowers)}
          className="flex items-center gap-2 text-sm text-[var(--cream-soft)] hover:text-[var(--cream)]"
          data-testid="fans-toggle-followers"
        >
          <Users className="w-4 h-4" />
          Kun følgere ({data.followers_only.length}) {showFollowers ? "▾" : "▸"}
        </button>
        {showFollowers && (
          <div className="mt-3 space-y-1">
            {followers.length === 0 ? (
              <p className="text-xs text-[var(--cream-faint)] italic pl-6">Ingen.</p>
            ) : followers.map((row) => (
              <FanRow key={row.user.id} row={row} checked={selected.has(row.user.id)} onToggle={() => toggle(row.user.id)} muted />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FanRow({ row, checked, onToggle, muted }) {
  const segNames = (row.segments || []).map((s) => s.name);
  return (
    <div
      className={`flex items-center gap-3 p-2 border border-white/5 ${muted ? "opacity-70" : ""} rounded`}
      data-testid={`fan-row-${row.user.username}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="accent-[var(--magenta)]"
        data-testid={`fan-check-${row.user.username}`}
      />
      <div className="w-9 h-9 rounded-full bg-[var(--noir-elev)] flex items-center justify-center overflow-hidden">
        {row.user.avatar_url ? (
          <img src={row.user.avatar_url} className="w-full h-full object-cover" alt="" />
        ) : (
          <span className="text-xs font-bold text-[var(--cream)]">
            {(row.user.username || "?")[0]?.toUpperCase()}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[var(--cream)] truncate">@{row.user.username}</div>
        <div className="text-[10px] text-[var(--cream-faint)] flex flex-wrap gap-1 mt-0.5">
          {segNames.length > 0 && segNames.map((n) => (
            <span key={n} className="px-1.5 py-0.5 border border-[var(--magenta)]/40 text-[var(--magenta)]">
              {n}
            </span>
          ))}
          {row.last_dm_at && <span>Sist DM: {new Date(row.last_dm_at).toLocaleDateString("nb-NO")}</span>}
        </div>
      </div>
    </div>
  );
}
